import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Typography,
    Container,
    CircularProgress,
    Button,
    Snackbar,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip,
    Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { AddCircle as AddCircleIcon } from '@mui/icons-material';
import axios from '../config/axiosConfig';
import { formatNumber } from '../utils/numberUtils';
import currencySymbols from '../utils/currencySymbols';
import AddToWatchlistModal from './AddToWatchlistModal';
import UpgradePopup from './UpgradePopup';
import TitleAndInfo from './TitleAndInfo';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const MarketCapList = ({ user }) => {
    const theme = useTheme();
    const { capSize } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [allStocks, setAllStocks] = useState([]);
    const [filteredStocks, setFilteredStocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [showAddToWatchlistModal, setShowAddToWatchlistModal] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage,] = useState('');
    const [showUpgradePopup, setShowUpgradePopup] = useState(false);
    
    // Filter and search states
    const [searchValue, setSearchValue] = useState('');
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [selectedExchanges, setSelectedExchanges] = useState([]);
    const [availableCurrencies, setAvailableCurrencies] = useState([]);
    const [availableExchanges, setAvailableExchanges] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    const baseUrl = window.location.origin;

    const marketCapInfo = {
        'mega-cap': 'Market capitalization of $200 billion or more',
        'large-cap': 'Market capitalization between $10 billion and $200 billion',
        'mid-cap': 'Market capitalization between $2 billion and $10 billion',
        'small-cap': 'Market capitalization between $300 million and $2 billion',
        'micro-cap': 'Market capitalization between $50 million and $300 million',
        'nano-cap': 'Market capitalization below $50 million'
    };

    const titleTop = `${capSize.charAt(0).toUpperCase() + capSize.slice(1)} Stocks`;
    const info = marketCapInfo[capSize] || 'Information not available';

    const columns = [
        {
            field: 'symbol',
            headerName: 'Company',
            minWidth: 250,
            renderCell: (params) => (
                <div>
                    <RouterLink
                        to={`${baseUrl}/asset/${params.row.symbol}`}
                        style={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                        }}
                    >
                        {params.row.symbol}
                    </RouterLink>
                    <div
                        style={{ fontSize: '0.9em', color: '#888', fontWeight: 'bold' }}
                    >
                        {params.row.name || 'N/A'}
                    </div>
                </div>
            ),
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'price',
            headerName: 'Price',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <div>
                    {`${currencySymbols[params.row.currency] || ''}${formatNumber(params.value)}`}
                </div>
            ),
        },
        {
            field: 'open',
            headerName: 'Open',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div>
                    {`${currencySymbols[params.row.currency] || ''}${formatNumber(params.value)}`}
                </div>
            ),
        },
        {
            field: 'dayHigh',
            headerName: 'High',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div>
                    {`${currencySymbols[params.row.currency] || ''}${formatNumber(params.value)}`}
                </div>
            ),
        },
        {
            field: 'dayLow',
            headerName: 'Low',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div>
                    {`${currencySymbols[params.row.currency] || ''}${formatNumber(params.value)}`}
                </div>
            ),
        },
        {
            field: 'changesPercentage',
            headerName: 'Change %',
            flex: 1,
            minWidth: 150,
            valueFormatter: (params) => formatNumber(params.value),
            renderCell: (params) => (
                <div style={{ color: params.value > 0 ? theme.palette.positive.main : params.value < 0 ? theme.palette.negative.main : theme.palette.lightGray.main }}>
                    {formatNumber(params.value)}%
                </div>
            ),
        },
        {
            field: 'change',
            headerName: 'Change',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={{ color: params.value > 0 ? theme.palette.positive.main : params.value < 0 ? theme.palette.negative.main : theme.palette.lightGray.main }}>
                    {`${currencySymbols[params.row.currency] || ''}${formatNumber(params.value)}`}
                </div>
            ),
        },
        {
            field: 'volume',
            headerName: 'Volume',
            flex: 1,
            minWidth: 150,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'marketCap',
            headerName: 'Market Cap',
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <div>
                    {`${currencySymbols[params.row.currency] || ''}${formatNumber(params.value)}`}
                </div>
            ),
        },
        {
            field: 'exchange',
            headerName: 'Exchange',
            flex: 1,
            minWidth: 120,
        },
        user && {
            field: 'addToWatchlist',
            headerName: '',
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleAddToWatchlist(params.row);
                    }}
                    sx={{ minWidth: 'unset', p: 1 }}
                >
                    <AddCircleIcon />
                </Button>
            ),
        },
    ].filter(Boolean);

    // Initialize filters from URL parameters
    useEffect(() => {
        const currencyFromUrl = searchParams.get('currency') || '';
        const exchangesFromUrl = searchParams.get('exchanges');
        const searchFromUrl = searchParams.get('search') || '';
        
        setSelectedCurrency(currencyFromUrl);
        setSelectedExchanges(exchangesFromUrl ? exchangesFromUrl.split(',') : []);
        setSearchValue(searchFromUrl);
    }, [searchParams]);

    // Apply filters and search to the data
    useEffect(() => {
        let filtered = [...allStocks];

        // Filter by search
        if (searchValue) {
            filtered = filtered.filter((stock) =>
                stock.symbol.toLowerCase().includes(searchValue.toLowerCase()) ||
                stock.name?.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        // Filter by currency
        if (selectedCurrency) {
            filtered = filtered.filter(stock => stock.currency === selectedCurrency);
        }

        // Filter by exchanges
        if (selectedExchanges.length > 0) {
            filtered = filtered.filter(stock => selectedExchanges.includes(stock.exchange));
        }

        setFilteredStocks(filtered);
        
        // Reset pagination to first page when filters change
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allStocks, searchValue, selectedCurrency, selectedExchanges]);

    useEffect(() => {
        const fetchStocks = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`/api/market-cap/${capSize}`);
                setAllStocks(response.data);
                
                // Extract unique currencies and exchanges with priority ordering
                const uniqueCurrencies = [...new Set(response.data.map(stock => stock.currency))].filter(Boolean);
                const uniqueExchanges = [...new Set(response.data.map(stock => stock.exchange))].filter(Boolean);
                
                // Priority order for currencies
                const currencyPriority = ['USD', 'EUR', 'GBP'];
                const currencies = [
                    ...currencyPriority.filter(curr => uniqueCurrencies.includes(curr)),
                    ...uniqueCurrencies.filter(curr => !currencyPriority.includes(curr)).sort()
                ];
                
                // Priority order for exchanges
                const exchangePriority = ['NASDAQ', 'NYSE', 'AMEX'];
                const exchanges = [
                    ...exchangePriority.filter(ex => uniqueExchanges.includes(ex)),
                    ...uniqueExchanges.filter(ex => !exchangePriority.includes(ex)).sort()
                ];
                
                setAvailableCurrencies(currencies);
                setAvailableExchanges(exchanges);
            } catch (error) {
                console.error('Error fetching stocks:', error);
                if (error.response) {
                    console.error('Error data:', error.response.data);
                    console.error('Error status:', error.response.status);
                    console.error('Error headers:', error.response.headers);
                } else if (error.request) {
                    console.error('Error request:', error.request);
                } else {
                    console.error('Error message:', error.message);
                }
                setError('Failed to fetch stocks. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchStocks();
    }, [capSize]);

    const handleAddToWatchlist = (asset) => {
        setSelectedAsset(asset);
        setShowAddToWatchlistModal(true);
    };

    const handleWatchlistSuccess = (message) => {
        // Optional: Handle success message if needed
        // console.log(message);
    };

    const handleCloseModal = () => {
        setShowAddToWatchlistModal(false);
        setSelectedAsset(null);
    };

    // Update URL parameters when filters change
    const updateUrlParams = (currency, exchanges, search) => {
        const newSearchParams = new URLSearchParams();
        
        if (currency) {
            newSearchParams.set('currency', currency);
        }
        
        if (exchanges.length > 0) {
            newSearchParams.set('exchanges', exchanges.join(','));
        }
        
        if (search) {
            newSearchParams.set('search', search);
        }
        
        // Use replace to avoid cluttering browser history
        setSearchParams(newSearchParams, { replace: true });
    };

    const handleSearchChange = (event) => {
        const newSearch = event.target.value;
        setSearchValue(newSearch);
        updateUrlParams(selectedCurrency, selectedExchanges, newSearch);
    };

    const handleCurrencyChange = (event) => {
        const newCurrency = event.target.value;
        setSelectedCurrency(newCurrency);
        updateUrlParams(newCurrency, selectedExchanges, searchValue);
    };

    const handleExchangeChange = (event) => {
        const value = event.target.value;
        const newExchanges = typeof value === 'string' ? value.split(',') : value;
        setSelectedExchanges(newExchanges);
        updateUrlParams(selectedCurrency, newExchanges, searchValue);
    };

    const clearFilters = () => {
        setSelectedCurrency('');
        setSelectedExchanges([]);
        setSearchValue('');
        // Clear URL parameters
        setSearchParams({}, { replace: true });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: theme.palette.background.default, color: theme.palette.text.primary, py: 4 }}>
            <Container maxWidth="xxl">
                <TitleAndInfo titleTop={titleTop} info={info} />
                
                {/* Filters Section */}
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                label="Search by Symbol or Company Name"
                                variant="outlined"
                                value={searchValue}
                                onChange={handleSearchChange}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={1.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Currency</InputLabel>
                                <Select
                                    value={selectedCurrency}
                                    onChange={handleCurrencyChange}
                                    input={<OutlinedInput label="Currency" />}
                                >
                                    <MenuItem value="">
                                        <em>All Currencies</em>
                                    </MenuItem>
                                    {availableCurrencies.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency} ({currencySymbols[currency] || currency})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Exchanges</InputLabel>
                                <Select
                                    multiple
                                    value={selectedExchanges}
                                    onChange={handleExchangeChange}
                                    input={<OutlinedInput label="Exchanges" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                    MenuProps={MenuProps}
                                >
                                    {availableExchanges.map((exchange) => (
                                        <MenuItem key={exchange} value={exchange}>
                                            {exchange}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={1}>
                            <Button 
                                variant="outlined" 
                                onClick={clearFilters}
                                disabled={!selectedCurrency && selectedExchanges.length === 0 && !searchValue}
                                size="small"
                            >
                                Clear All
                            </Button>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={5.5}>
                            <Typography variant="body2" color="textSecondary">
                                Showing {filteredStocks.length} of {allStocks.length} stocks
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <DataGrid
                    rows={filteredStocks}
                    columns={columns}
                    getRowId={(row) => row.symbol}
                    disableColumnMenu
                    paginationPosition="middle"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    initialState={{
                        sorting: { sortModel: [{ field: 'marketCap', sort: 'desc' }] },
                    }}
                    pageSizeOptions={[5, 10, 25]}
                />
            </Container>
            <Snackbar
                open={showSnackbar}
                autoHideDuration={3000}
                onClose={() => setShowSnackbar(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
            <UpgradePopup
                open={showUpgradePopup}
                onClose={() => setShowUpgradePopup(false)}
            />

            {selectedAsset && (
                <AddToWatchlistModal
                    open={showAddToWatchlistModal}
                    onClose={handleCloseModal}
                    asset={selectedAsset}
                    userId={user?._id}
                    onSuccess={handleWatchlistSuccess}
                    assetType="stock"
                />
            )}
        </Box>
    );
};

export default MarketCapList;